import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { fetchAnalyses, fetchAnalysisById } from './api';
import {
  AnalysisListResponse,
  AnalysisDetail,
  AnalysisListParams
} from './types';

// Query Keys
export const analysisKeys = {
  all: ['analyses'] as const,
  lists: () => [...analysisKeys.all, 'list'] as const,
  list: (params?: AnalysisListParams) =>
    [...analysisKeys.lists(), params] as const,
  details: () => [...analysisKeys.all, 'detail'] as const,
  detail: (id: string | null | undefined) =>
    [...analysisKeys.details(), id] as const
};

/**
 * Hook to fetch list of analyses
 */
export function useAnalyses(
  params?: AnalysisListParams,
  options?: Omit<
    UseQueryOptions<AnalysisListResponse, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: analysisKeys.list(params),
    queryFn: () => fetchAnalyses(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options
  });
}

/**
 * Hook to fetch single analysis with findings
 */
export function useAnalysis(
  analysisId: string | null | undefined,
  options?: Omit<
    UseQueryOptions<AnalysisDetail, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: analysisKeys.detail(analysisId),
    queryFn: async () => {
      const result = await fetchAnalysisById(analysisId!);
      return result;
    },
    enabled: !!analysisId,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      const shouldRefetch =
        data?.status === 'PROCESSING' || data?.status === 'PENDING';

      return shouldRefetch ? 3000 : false;
    },
    ...options
  });
}
